/**
 * The journal derivation (R16) — every document the player has read, in the
 * order they read them, ready to render.
 *
 * @remarks
 * The `deriveMapGraph` precedent, document-side: the engine owns the
 * derivation, `player-ui` renders it, and there is no journal STATE — the
 * journal is a view over `narrative.readLogIds` (the mark the first read
 * leaves, R12) joined against the bundle's carrier items, so it can never
 * disagree with what was actually read.
 *
 * Readings are LIVE (Mike's R16 ruling): a document's conditional lines
 * resolve against the current world through the one describer, because the
 * journal is the player re-reading the document now — the same text `inspect`
 * would speak — not a second stored copy of what was said once. The transcript
 * remains the frozen record; the journal is the shelf.
 *
 * Ordering is the read order, which is deterministic by construction:
 * `readLogIds` appends on first read and never reorders.
 *
 * @packageDocumentation
 */

import { containerActorIdOf, containerItemIdOf, type ItemId, type LogId, type RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { EngineInvariantError } from '../execution/engine-invariant-error.js';
import { type ResolvedNarrativeLine, resolveNarrativeLines } from './conditional-text.js';

/** One read document, ready to render. */
export interface JournalEntry {
	/**
	 * The authored name of the room the document was found in (J2/D80, Mike's
	 * ruling): the room whose placements hold its carrier, walked up through
	 * containers when it ships inside another thing. Derived from the pack —
	 * provenance costs no authoring — and absent for a carrier no room
	 * placement reaches. The two POC documents were written by the same hand,
	 * and WHERE they lived (pinned behind glass; hidden behind a pipe) is the
	 * story the shelf can now tell.
	 */
	readonly foundInRoomName?: string;
	/** The document's lines, readings chosen against the current world. */
	readonly lines: readonly ResolvedNarrativeLine[];
	readonly logId: LogId;
	/**
	 * What the shelf calls the document: its authored `title` (J1/D78), or —
	 * for a document that authored none — its carrier item's name, so every
	 * pre-D78 pack reads exactly as it did.
	 */
	readonly title: string;
}

/**
 * Derive the journal: every read document, in read order, with live readings.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The read documents, oldest first (empty until something is read)
 */
export const deriveJournal = (bundle: EngineGameBundle, state: RuntimeState): readonly JournalEntry[] =>
	state.narrative.readLogIds.map((logId) => {
		// No logsById index exists: logs live ON their carrier (R12's
		// `containedIn` move), so the carrier scan IS the lookup. Pack
		// declaration order makes it deterministic; the loader's semantic gate
		// makes it total — a read id with no carrier is a corrupt session.
		for (const item of bundle.indexes.itemsById.values()) {
			if (item.log?.logId === logId) {
				const foundInRoomName = homeRoomNameOf(bundle, item.itemId);

				return {
					...(foundInRoomName === undefined ? {} : { foundInRoomName }),
					lines: resolveNarrativeLines(item.log.narratives, bundle, state),
					logId,
					title: item.log.title ?? item.name,
				};
			}
		}

		throw new EngineInvariantError(
			`readLogIds names "${logId}", but no item in the bundle carries that log — the loader's semantic gate and the save opener should each have refused this`,
		);
	});

/**
 * The room a document's carrier item calls home (J2/D80): the room whose
 * placements hold it, walked up through its containers when it ships inside
 * another thing.
 *
 * @param bundle - The loaded bundle
 * @param carrierItemId - The document's carrier item
 * @returns The home room's authored name, or `undefined` for a carrier no room placement reaches (D240: or that a body carries)
 */
const homeRoomNameOf = (bundle: EngineGameBundle, carrierItemId: ItemId): string | undefined => {
	let placedItemId = carrierItemId;

	// The container walk is bounded by the item roster: the loader's semantic
	// gate refuses unknown container ids, so only a container CYCLE could
	// outlast the roster — and the bound turns that corruption into "no home"
	// rather than a hang.
	for (let containerHop = 0; containerHop < bundle.indexes.itemsById.size; containerHop += 1) {
		// D240: a thing a body carries was found on the body, not in a room — the walk ends with no home.
		const { containedIn } = requireItem(bundle, placedItemId);

		if (containerActorIdOf(containedIn) !== undefined) {
			return undefined;
		}

		const containerItemId = containerItemIdOf(containedIn);

		if (containerItemId === undefined) {
			break;
		}

		placedItemId = containerItemId;
	}

	return bundle.contentPack.rooms.find((room) => room.itemPlacements.includes(placedItemId))?.name;
};
